import { memo } from "react";
import { Modal } from "@vilocnv/allsetra-core";
import { useTripBackupReminder } from "./useTripBackupReminder";
import { TripBackupReminderModalContent } from "./TripBackupReminderModalContent";
import { useTranslation } from "react-i18next";

export interface TripBackupReminderModalProps {
  accountId: string;
}

export const TripBackupReminderModal = memo(
  ({ accountId }: TripBackupReminderModalProps) => {
    const {
      open,
      theme,
      page,
      hasViewedXarPage,
      showXarPage,
      showXarCta,
      isAdmin,
      handleDismiss,
      handleNext,
      handleBack,
      handleGoToXarReports
    } = useTripBackupReminder(accountId);

    const { t } = useTranslation();

    if (!open) return null;

    const isPage1 = page === 1;
    // When XAR awareness is required, dismiss stays blocked until the XAR page is viewed
    const mustViewXarBeforeDismiss = showXarPage && !hasViewedXarPage;

    const primaryBtnProps =
      showXarPage && isPage1
        ? {
            children: t("tripBackupReminder.next"),
            onClick: handleNext
          }
        : {
            children: t("tripBackupReminder.dismiss"),
            onClick: () => handleDismiss()
          };

    // Modal from allsetra-core always mounts the secondary button; never omit
    // props or it renders an empty broken text button.
    const secondaryBtnProps = mustViewXarBeforeDismiss
      ? {
          children: t("buttonsText.close"),
          disabled: true,
          tabIndex: -1,
          "aria-hidden": true,
          onClick: () => undefined,
          sx: { display: "none" }
        }
      : showXarPage
        ? isPage1
          ? {
              children: t("buttonsText.close"),
              onClick: () => handleDismiss()
            }
          : {
              children: t("tripBackupReminder.back"),
              onClick: handleBack
            }
        : {
            children: t("buttonsText.close"),
            onClick: () => handleDismiss()
          };

    return (
      <Modal
        open={open}
        onClose={
          mustViewXarBeforeDismiss ? () => undefined : () => handleDismiss()
        }
        theme={theme}
        primaryBtnProps={primaryBtnProps}
        secondaryBtnProps={secondaryBtnProps}
        // allsetra-core Modal always renders the header close IconButton;
        // hide it until the XAR page has been viewed (when dismiss is allowed).
        modalStyle={
          mustViewXarBeforeDismiss
            ? { "& .MuiIconButton-root": { display: "none" } }
            : undefined
        }
      >
        <TripBackupReminderModalContent
          page={page}
          showXarPage={showXarPage}
          showXarCta={showXarCta}
          isAdmin={isAdmin}
          onGoToXarReports={handleGoToXarReports}
        />
      </Modal>
    );
  }
);

TripBackupReminderModal.displayName = "TripBackupReminderModal";
